import React, { Dispatch, SetStateAction } from 'react';

import SearchNoResults from '@/assets/icons/search-no-results.svg';

import { IMeetingplacePageHome } from '@/lib/fragments/meetingPlacePageHome';
import { ClickHistory } from '../floor-plan/floor-plan';
import {
  ActiveBuilding,
  FilterTile,
  MapTile,
} from '../floor-plan-map/floor-plan-map.interface';
import InputField, {
  InputFieldTrailingIcon,
} from '../ui/input-field/input-field';
import { FloorPlanFilterCategories } from './floor-plan-filter-categories/floor-plan-filter-categories';
import { FloorPlanFilterListStyle } from './floor-plan-filter-list-style.type';
import { FloorPlanFilterListType } from './floor-plan-filter-list-type.type';
import { FloorPlanFilterTile } from './floor-plan-filter-tile/floor-plan-filter-tile';
import { useGetLibraryConstantsDictionaryData } from '@/providers/ConfigProvider';

interface FloorPlanFilterContentProps {
  activeTiles: FilterTile[];
  autoFocusInput?: boolean;
  listStyle: FloorPlanFilterListStyle;
  updateFilterList?: Dispatch<SetStateAction<FloorPlanFilterListType>>;
  filterValue: string;
  clickHistory: ClickHistory[];
  handleBackClick: () => void;
  filterFieldIcon: InputFieldTrailingIcon;
  setFilterValue: Dispatch<SetStateAction<string>>;
  inputFocus: () => void;
  filterDrawerOpen: boolean;
  activeStore: MapTile | undefined;
  setActiveStore: Dispatch<SetStateAction<MapTile | undefined>>;
  setClickHistory: Dispatch<SetStateAction<ClickHistory[]>>;
  setFilterDrawerOpen: Dispatch<SetStateAction<boolean>>;
  clickOnTenant: (id: FilterTile) => Promise<void>;
  goToFloor: (id?: string) => Promise<void>;
  checkFloorOfID: (id: string) => string | false | undefined;
  getLocalizedShortName: (id: string) => string | false | undefined;
  performCategoryListing?: (
    category: string,
    type?: FloorPlanFilterListType,
  ) => void;
  homepage: IMeetingplacePageHome;
  node_locale: string;
  activeBuilding: ActiveBuilding | undefined;
  handleClearField?: () => void;
}

export const FloorPlanFilterContent = ({
  filterValue,
  clickHistory,
  handleBackClick,
  filterFieldIcon,
  setFilterValue,
  inputFocus,
  filterDrawerOpen,
  listStyle,
  activeTiles,
  updateFilterList,
  activeStore,
  activeBuilding,
  setActiveStore,
  setClickHistory,
  clickOnTenant,
  goToFloor,
  checkFloorOfID,
  getLocalizedShortName,
  performCategoryListing,
  node_locale,
  handleClearField,
}: FloorPlanFilterContentProps) => {
  const dictionary = useGetLibraryConstantsDictionaryData();

  return (
    <>
      <div
        className={`flex
        items-center justify-between`}
        id="filterControls"
      >
        <InputField
          id="floorplan-input"
          leftSideIcon={
            filterValue.length === 0 && clickHistory.length !== 0
              ? 'Back'
              : undefined
          }
          leftSideIconOnClick={handleBackClick}
          label="Search"
          visuallyHideLabel={true}
          icon={filterFieldIcon}
          type="text"
          name="filter"
          onChange={(e) => setFilterValue(e.target.value)}
          value={filterValue}
          className={`relative ${filterDrawerOpen ? 'z-50' : 'z-10'}
           flex flex-grow text-text-default md:flex`}
          placeholder={
            clickHistory.length > 0
              ? clickHistory.slice(-1)[0].title
              : dictionary?.search || 'Search'
          }
          autoComplete={false}
          clearField={
            handleClearField ||
            (() => {
              setFilterValue('');
              setClickHistory([]);
              performCategoryListing?.('');
            })
          }
          onFocus={inputFocus}
        />
      </div>
      <div
        id="filterDrawer"
        className={`scrollbar-mb mt-2
            max-h-[calc(100vh_-_260px)]
            overflow-auto
       pb-2 md:max-h-[calc(100%_-_45px)] md:pl-2 ${
         filterDrawerOpen ? 'flex flex-wrap' : 'hidden'
       }`}
      >
        {listStyle === 'tile' &&
          activeTiles
            .sort((a, b) => a.title?.localeCompare(b.title || '') || 0)
            .map((tile, index) => (
              <FloorPlanFilterTile
                key={index}
                tile={tile}
                updateFilterList={updateFilterList}
                activeStore={activeStore}
                setActiveStore={setActiveStore}
                activeBuilding={activeBuilding}
                filterValue={filterValue}
                setClickHistory={setClickHistory}
                clickHistory={clickHistory}
                clickOnTenant={clickOnTenant}
                goToFloor={goToFloor}
                checkFloorOfID={checkFloorOfID}
                getLocalizedShortName={getLocalizedShortName}
                filterByCategory={performCategoryListing}
                isLastTile={index === activeTiles.length - 1}
                node_locale={node_locale}
              />
            ))}

        {listStyle === 'category' &&
          activeTiles[0]?.categories &&
          activeTiles[0]?.type && (
            <FloorPlanFilterCategories
              categories={activeTiles[0].categories}
              filterByCategory={performCategoryListing}
              type={activeTiles[0].type}
              setClickHistory={setClickHistory}
              clickHistory={clickHistory}
            />
          )}
        {activeTiles.length === 0 && (
          <div className="flex w-full flex-col items-center justify-center px-8 text-center">
            <SearchNoResults className="mt-8 lg:mt-12" />
            <div className="mt-8 font-title text-headings text-text-default">
              {dictionary?.noSearchResultsMainFloorPlan}
            </div>
            <div className="mt-8 text-bodym">
              {dictionary?.noSearchResultsSecondaryFloorPlan}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
